import React from "react";
import { useNavigate } from "react-router-dom";
import { colors2, UI_BODY_SM, UI_BODY_SEMIBOLD_SM } from "oolib";
import {
  ButtonPrimaryCompact,
  ButtonTertiaryCompact,
} from "oolib/dist/v2/components/Buttons";
import { DisplayIcon } from "oolib/dist/utils/comps/DisplayIcon";
import {
  Card,
  CardHeader,
  Row,
  Actions,
  DatasetRow,
  IconButton,
  INHERIT_CURSOR,
  Divider,
  redOutline,
} from "./styled";
import { ScheduleLabel } from "./common";

// Row-level Sync now: an icon, with the reason it can't run as its tooltip.
const RowSyncButton = ({ dataset, onSync }) => (
  <span title={dataset.syncBlockedReason || "Sync now"}>
    <IconButton
      aria-label="Sync now"
      disabled={!!dataset.syncBlockedReason || dataset.syncing}
      onClick={() => onSync(dataset)}
    >
      <DisplayIcon icon="ArrowsClockwise" size={16} style={INHERIT_CURSOR} />
    </IconButton>
  </span>
);

/**
 * "Connected Datasets (n)" card in a manage popup. Each row shows its schedule
 * at rest and its actions on hover. Sync shows only with canSync and
 * canPublish; Add New Dataset only with canPublish. Configure shows only with
 * onConfigure; Delete only with onDelete, on rows marked canDelete; Delete All
 * only with onDeleteAll.
 */
export const DatasetsCard = ({
  name,
  datasets,
  canSync,
  canPublish,
  onAddDataset,
  onConfigure,
  onSync,
  onDelete,
  onDeleteAll,
}) => {
  const navigate = useNavigate();
  return (
    <Card>
      <CardHeader>
        <UI_BODY_SEMIBOLD_SM>{`Connected Datasets (${datasets.length})`}</UI_BODY_SEMIBOLD_SM>
        <Actions>
          {onDeleteAll && (
            <ButtonTertiaryCompact
              icon="Trash"
              iconColor={colors2.red}
              style={redOutline}
              onClick={onDeleteAll}
            >
              Delete All
            </ButtonTertiaryCompact>
          )}
          {canPublish && (
            <ButtonPrimaryCompact icon="Plus" onClick={onAddDataset}>
              Add New Dataset
            </ButtonPrimaryCompact>
          )}
        </Actions>
      </CardHeader>
      {datasets.length === 0 && (
        <Row style={{ justifyContent: "center", padding: "24px 14px" }}>
          <UI_BODY_SM style={{ color: colors2.grey60 }}>
            {`${name} is authenticated but not importing into any dataset yet.`}
          </UI_BODY_SM>
        </Row>
      )}
      {datasets.map((d) => (
        <DatasetRow key={d.id}>
          <UI_BODY_SM style={{ color: colors2.grey60 }}>{d.title}</UI_BODY_SM>
          <ScheduleLabel schedule={d.schedule} />
          <Actions className="rowActions">
            <IconButton
              aria-label="Open dataset"
              onClick={() => navigate(`/datasets/${d.id}`)}
            >
              <DisplayIcon
                icon="ArrowLineUpRight"
                size={16}
                style={INHERIT_CURSOR}
              />
            </IconButton>
            <Divider />
            {canSync && canPublish && (
              <RowSyncButton dataset={d} onSync={onSync} />
            )}
            {onConfigure && (
              <ButtonTertiaryCompact
                icon="PencilSimple"
                style={{ background: colors2.white }}
                onClick={() => onConfigure(d)}
              >
                Configure
              </ButtonTertiaryCompact>
            )}
            {onDelete && d.canDelete && (
              <IconButton
                aria-label="Delete dataset"
                title="Delete dataset"
                onClick={() => onDelete(d)}
              >
                <DisplayIcon icon="Trash" size={16} style={INHERIT_CURSOR} />
              </IconButton>
            )}
          </Actions>
        </DatasetRow>
      ))}
    </Card>
  );
};

export default DatasetsCard;
